import { useState, useEffect } from "react";
import { fetchFirstPassRate } from "../../utils/dataReceiver";

/**
 * Fetch and manage state for the First Pass Rate (FPR) modal.
 *
 * Fires once on mount. Everything returned is scoped to the current calendar
 * month: a daily FPR trend (one point per day of the month), a table of jobs
 * that failed FPR this month, and a utility-station failure count table.
 * Days after today (or with no evaluated items) carry fpr: null so the line
 * chart terminates cleanly at today.
 *
 * Cancels the in-flight request automatically if the component unmounts before
 * the response arrives (AbortController cleanup).
 *
 * Cache TTL: 5 minutes - FPR data updates at most once per day but the modal
 * may be opened repeatedly within a session.
 *
 * FPR definition: an item passes first pass if it has no qualifying
 * nonconformant steps at utility stations (8 = Fire Sprinkler, 20 = Plumbing,
 * 22 = Electrical), excluding steps whose only reason matches
 * "Moving mod to next station". FPR % = passes / total × 100.
 *
 * Note: targetFpr is sourced live from RTLS_ProductionGoals (PrimaryName =
 * 'firstPassRate'). GoalValue is stored as a decimal fraction server-side
 * (0.95 → 95) and is already expanded in the API response.
 *
 * @returns {{
 *   metrics: {
 *     currentMonthFpr:  number,   - FPR % for the current month, 1 dp, default 100
 *     targetFpr:        number,   - goal from RTLS_ProductionGoals (e.g. 95)
 *     weeksMet:         number,   - weeks this month where weekly FPR >= target
 *     weeksUnder:       number,   - weeks this month where weekly FPR < target
 *   },
 *   graph: Array<{
 *     day:    number,          - 1 … (days in current month)
 *     label:  string,          - "Jul 1"
 *     fpr:    number | null,   - null for future days or days with no data
 *     target: number,          - targetFpr echoed on every entry for chart reference line
 *   }>,
 *   tableFailedJobs: Array<{
 *     woNum:          string,     - work order number
 *     itemNum:        string,     - item number
 *     batchNum:       string,     - batch number
 *     failedStations: string[],   - e.g. ["Fire Sprinkler", "Electrical"]
 *     day:            string,     - "YYYY-MM-DD" evaluation date
 *   }>,
 *   tableUtilities: Array<{
 *     utility:    string,   - "Fire Sprinkler" | "Plumbing" | "Electrical"
 *     stationId:  number,   - 8 | 20 | 22
 *     failCount:  number,   - items with a qualifying fail at this station, current month
 *     totalItems: number,   - items that passed through this station, current month
 *     failPct:    number,   - failCount / totalItems × 100, 1 dp
 *   }>,
 *   loading: boolean,
 *   error:   string | null,
 * }}
 */

let _cache = null;
let _cacheTime = 0;
const CACHE_TTL_MS = 5 * 60_000; // 5 minutes

const METRICS_DEFAULT = {
  currentMonthFpr: 100,
  targetFpr: 95,
  weeksMet: 0,
  weeksUnder: 0,
};

const useFirstPassRateData = () => {
  const [metrics, setMetrics] = useState(_cache?.metrics ?? METRICS_DEFAULT);
  const [graph, setGraph] = useState(_cache?.graph ?? []);
  const [tableFailedJobs, setTableFailedJobs] = useState(
    _cache?.tableFailedJobs ?? [],
  );
  const [tableUtilities, setTableUtilities] = useState(
    _cache?.tableUtilities ?? [],
  );
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const isStale = !_cache || Date.now() - _cacheTime > CACHE_TTL_MS;
    if (!isStale) return; // already fresh - skip fetch

    const load = async () => {
      try {
        const data = await fetchFirstPassRate(controller.signal);
        _cache = data;
        _cacheTime = Date.now();
        setMetrics(data.metrics);
        setGraph(data.graph);
        setTableFailedJobs(data.tableFailedJobs);
        setTableUtilities(data.tableUtilities);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message ?? "Failed to load first pass rate data.");
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, []);

  return {
    metrics,
    graph,
    tableFailedJobs,
    tableUtilities,
    loading,
    error,
  };
};

export default useFirstPassRateData;
